import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Building2,
  Users,
  Award,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Search,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { useState, useRef, useEffect } from "react";

// ألوان القطاعات - تعريف خارج المكون
const sectorColors = {
  "قطاع الصناعات المتقدمة": {
    gradient: "from-purple-500 to-pink-500",
    bg: "bg-purple-500",
    light: "bg-purple-50",
    border: "border-purple-200",
  },
  "قطاع الأمن الغذائي": {
    gradient: "from-green-500 to-emerald-500",
    bg: "bg-green-500",
    light: "bg-green-50",
    border: "border-green-200",
  },
  "قطاع الطاقة": {
    gradient: "from-orange-500 to-red-500",
    bg: "bg-orange-500",
    light: "bg-orange-50",
    border: "border-orange-200",
  },
  "قطاع المقاولات والخدمات": {
    gradient: "from-blue-500 to-cyan-500",
    bg: "bg-blue-500",
    light: "bg-blue-50",
    border: "border-blue-200",
  },
  default: {
    gradient: "from-gray-500 to-blue-500",
    bg: "bg-gray-500",
    light: "bg-gray-50",
    border: "border-gray-200",
  },
};

// دالة مساعدة - تعريف خارج المكون
const getSectorColor = (sectorName) => {
  return sectorColors[sectorName] || sectorColors.default;
};

const getSectorIcon = (sectorName) => {
  const icons = {
    "قطاع الصناعات المتقدمة": "🚀",
    "قطاع الأمن الغذائي": "🌱",
    "قطاع الطاقة": "⚡",
    "قطاع المقاولات والخدمات": "🏗️",
    "Advanced Industries Sector": "🚀",
    "Food Security Sector": "🌱",
    "Energy Sector": "⚡",
    "Construction and Services Sector": "🏗️",
    "Secteur des industries avancées": "🚀",
    "Secteur de la sécurité alimentaire": "🌱",
    "Secteur de l'énergie": "⚡",
    "Secteur de la construction et des services": "🏗️",
  };

  return icons[sectorName] || "💼";
};

const Sectors = () => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const { seedData } = useStore();

  const lng = i18n.language || "en";
  const isRTL = lng === "ar";

  const [expandedSectors, setExpandedSectors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSector, setSelectedSector] = useState("all");
  const [hoveredCompany, setHoveredCompany] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchInputRef = useRef(null);

  function goToProfile(companyId) {
    navigate(`/sectors/${companyId}`);
  }

  const sectorsData = seedData?.companies || [];

  // إحصائيات ديناميكية
  const stats = [
    {
      icon: Building2,
      value: sectorsData.reduce(
        (acc, sector) => acc + (sector.companies?.length || 0),
        0
      ),
      label:
        lng === "ar"
          ? "شركة مسجلة"
          : lng === "fr"
          ? "entreprises"
          : "companies",
    },
    {
      icon: Users,
      value: "50K+",
      label: lng === "ar" ? "موظف" : lng === "fr" ? "employés" : "employees",
    },
    {
      icon: Award,
      value: "100+",
      label:
        lng === "ar"
          ? "جائزة تميز"
          : lng === "fr"
          ? "prix d'excellence"
          : "awards",
    },
    {
      icon: TrendingUp,
      value: "25%",
      label:
        lng === "ar"
          ? "نمو سنوي"
          : lng === "fr"
          ? "croissance annuelle"
          : "annual growth",
    },
  ];

  const getSectorText = (sector, key) => {
    const langKey = `${key}_${lng}`;
    return sector[langKey] || sector[`${key}_ar`] || "";
  };

  const getCompanyName = (company) => {
    return company[lng] || company.ar || company.en || "";
  };

  const toggleSectorExpansion = (sectorIndex) => {
    setExpandedSectors((prev) => ({
      ...prev,
      [sectorIndex]: !prev[sectorIndex],
    }));
  };

  // فلترة الشركات مع نتائج البحث
  const filteredSectors = sectorsData.filter((sector) => {
    const sectorName = getSectorText(sector, "sector").toLowerCase();
    const matchesSearch = sectorName.includes(searchTerm.toLowerCase());
    const matchesSector =
      selectedSector === "all" || sector.sector_ar === selectedSector;
    return matchesSearch && matchesSector;
  });

  // البحث في جميع الشركات
  const searchResults = sectorsData.flatMap(
    (sector) =>
      sector.companies
        ?.filter((company) => {
          const companyName = getCompanyName(company).toLowerCase();
          const companyEnName = company.en?.toLowerCase() || "";
          return (
            companyName.includes(searchTerm.toLowerCase()) ||
            companyEnName.includes(searchTerm.toLowerCase())
          );
        })
        .map((company) => ({
          ...company,
          sectorName: getSectorText(sector, "sector"),
          sectorColor: getSectorColor(sector.sector_ar), // الآن يمكن الوصول للدالة
        })) || []
  );

  // تأثيرات البحث
  useEffect(() => {
    setShowSearchResults(searchTerm.length > 0);
  }, [searchTerm]);

  const clearSearch = () => {
    setSearchTerm("");
    setShowSearchResults(false);
    searchInputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative">
      {/* Hero Section معدل */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-blue-50 to-cyan-50"
      >
        {/* صورة خلفية كبيرة متحركة - معدلة */}
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1 }}
          animate={{
            scale: [1, 1.05, 1],
            x: [0, 15, -15, 0],
            y: [0, -10, 10, 0],
          }}
          transition={{
            scale: { duration: 8, repeat: Infinity, ease: "easeInOut" },
            x: { duration: 12, repeat: Infinity, ease: "easeInOut" },
            y: { duration: 15, repeat: Infinity, ease: "easeInOut" },
          }}
        >
          {/* استخدم import للصورة بدلاً من المسار النسبي */}
          <img
            src="assets/images/NSPO-Logo.webp" // public asset with base prefix
            alt="خلفية القطاعات"
            className="w-full h-full object-cover object-center opacity-20"
            onError={(e) => {
              console.error("Failed to load background image");
              // صورة بديلة إذا فشل التحميل
              e.target.src =
                "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2072&q=80";
            }}
          />
          {/* طبقة شفافة لتوضيح الصورة */}
          <div className="absolute inset-0 bg-white/20"></div>
        </motion.div>

        {/* تأثيرات إضافية سريعة */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              rotate: [0, 360],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute top-20 left-20 w-40 h-40 opacity-15"
          ></motion.div>

          <motion.div
            animate={{
              y: [0, -40, 0],
              rotate: [0, 180, 360],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute bottom-32 right-32 w-32 h-32 opacity-15"
          ></motion.div>

          {/* خطوط متقاطعة سريعة */}
          <motion.div
            animate={{
              scale: [1, 1.4, 1],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
            }}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 opacity-15"
          ></motion.div>
        </div>

        {/* طبقة لتحسين قراءة النص - مخففة */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-cyan-50/30 backdrop-blur-[0.5px]"></div>

        {/* المحتوى الرئيسي */}
        <div className="relative z-10 text-center px-4 max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12"
          >
            {/* أيقونة زخرفية */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mb-8"
            >
              <div className="w-24 h-24 mx-auto bg-white/90 backdrop-blur-md rounded-2xl shadow-xl flex items-center justify-center border border-white/50">
                <svg className="w-12 h-12" viewBox="0 0 100 100" fill="none">
                  <path
                    d="M30,30 L70,30 L70,70 L30,70 Z M40,40 L60,40 L60,60 L40,60 Z"
                    stroke="#3b82f6"
                    strokeWidth="3"
                    fill="none"
                  />
                  <circle cx="50" cy="50" r="8" fill="#06b6d4" opacity="0.8" />
                </svg>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-5xl md:text-7xl font-black text-gray-900 mb-6 leading-tight drop-shadow-sm"
            >
              <span className="bg-gradient-to-r from-gray-900 via-blue-800 to-cyan-700 bg-clip-text text-transparent">
                {lng === "ar"
                  ? "القطاعات"
                  : lng === "fr"
                  ? "Secteurs"
                  : "Sectors"}
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-xl md:text-2xl text-gray-800 max-w-4xl mx-auto leading-relaxed font-semibold bg-white/80 backdrop-blur-md rounded-2xl py-5 px-8 shadow-lg border border-white/50"
            >
              {lng === "ar"
                ? "اكتشف عالم الشركات الرائدة في مختلف القطاعات"
                : lng === "fr"
                ? "Découvrez le monde des entreprises leaders dans divers secteurs"
                : "Discover the world of leading companies across various sectors"}
            </motion.p>
          </motion.div>

          {/* شريط البحث */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="max-w-4xl mx-auto mt-12 relative"
          >
            <div className="relative">
              {/* أيقونة البحث */}
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 w-6 h-6 z-10">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 transition-colors z-10"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}

              <input
                ref={searchInputRef}
                type="text"
                placeholder={
                  lng === "ar"
                    ? "ابحث عن شركة أو قطاع..."
                    : lng === "fr"
                    ? "Rechercher une entreprise ou un secteur..."
                    : "Search for a company or sector..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-12 py-5 rounded-2xl border-2 border-gray-400 bg-white/95 backdrop-blur-lg text-gray-900 placeholder-gray-600 focus:outline-none focus:border-blue-500 focus:bg-white focus:shadow-2xl transition-all duration-300 text-lg shadow-xl font-medium"
              />
            </div>

            {/* نتائج البحث */}
            <AnimatePresence>
              {showSearchResults && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="absolute top-full left-0 right-0 mt-4 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-300 overflow-hidden z-50"
                >
                  <div className="p-4 max-h-96 overflow-y-auto">
                    {searchResults.length > 0 ? (
                      <div className="space-y-3">
                        <div className="text-sm text-gray-700 font-semibold px-2 pb-2 border-b border-gray-200">
                          {lng === "ar"
                            ? `تم العثور على ${searchResults.length} نتيجة`
                            : lng === "fr"
                            ? `${searchResults.length} résultats trouvés`
                            : `${searchResults.length} results found`}
                        </div>
                        {searchResults.map((company, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => goToProfile(company.id || index)}
                            className="flex items-center gap-4 p-4 rounded-xl hover:bg-blue-50 cursor-pointer transition-all duration-200 group border border-transparent hover:border-blue-300 hover:shadow-md"
                          >
                            {company.logo && (
                              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-300 group-hover:border-blue-400 transition-colors">
                                <img
                                  src={
                                    new URL(company.logo, import.meta.url).href
                                  }
                                  alt={getCompanyName(company)}
                                  className="w-8 h-8 object-contain"
                                  onError={(e) =>
                                    (e.target.style.display = "none")
                                  }
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-1">
                                <span className="font-bold text-gray-900 text-lg group-hover:text-blue-700 transition-colors">
                                  {getCompanyName(company)}
                                </span>
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md">
                                  {company.sectorName}
                                </span>
                              </div>
                              {company.en && lng !== "en" && (
                                <span className="text-gray-700 text-sm block font-medium">
                                  {company.en}
                                </span>
                              )}
                            </div>
                            <div className="w-5 h-5 text-gray-500 group-hover:text-blue-600 transition-colors">
                              <svg
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <svg
                          className="w-16 h-16 text-gray-500 mx-auto mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        <p className="text-gray-600 text-lg font-medium">
                          {lng === "ar"
                            ? "لم نعثر على نتائج مطابقة"
                            : lng === "fr"
                            ? "Aucun résultat trouvé"
                            : "No matching results found"}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.section>

      {/* شبكة القطاعات المعدلة */}
      <section id="sectors" className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              <span className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {lng === "ar"
                  ? "القطاعات المتاحة"
                  : lng === "fr"
                  ? "Secteurs Disponibles"
                  : "Available Sectors"}
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {lng === "ar"
                ? "استكشف مجموعة متنوعة من القطاعات الاقتصادية والصناعية"
                : lng === "fr"
                ? "Explorez une variété de secteurs économiques et industriels"
                : "Explore a variety of economic and industrial sectors"}
            </p>
          </motion.div>

          {/* فلترة القطاعات */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex flex-wrap gap-4 justify-center mb-12"
          >
            <button
              onClick={() => setSelectedSector("all")}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                selectedSector === "all"
                  ? "bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white shadow-lg"
                  : "bg-white text-gray-700 shadow-md hover:shadow-lg"
              }`}
            >
              {lng === "ar" ? "الكل" : lng === "fr" ? "Tous" : "All"}
            </button>
            {sectorsData.map((sector, index) => {
              const sectorColor = getSectorColor(sector.sector_ar);
              return (
                <button
                  key={index}
                  onClick={() => setSelectedSector(sector.sector_ar)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    selectedSector === sector.sector_ar
                      ? `${sectorColor.bg} text-white shadow-lg`
                      : "bg-white text-gray-700 shadow-md hover:shadow-lg"
                  }`}
                >
                  {getSectorText(sector, "sector")}
                </button>
              );
            })}
          </motion.div>

          {/* عرض القطاعات */}
          <div className="grid lg:grid-cols-2 xl:grid-cols-2 gap-8">
            {filteredSectors.map((sector, index) => {
              const isExpanded = expandedSectors[index];
              const sectorColor = getSectorColor(sector.sector_ar);
              const companiesToShow = isExpanded
                ? sector.companies
                : sector.companies?.slice(0, 4);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-3xl bg-white shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-200"
                >
                  {/* Header */}
                  <div
                    className={`bg-gradient-to-r ${sectorColor.gradient} p-6 relative overflow-hidden`}
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-12 translate-x-12"></div>
                    <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full translate-y-10 -translate-x-10"></div>

                    <div className="relative z-10 flex items-center gap-4">
                      <div className="text-4xl bg-white/20 backdrop-blur-sm rounded-2xl p-3 border border-white/30">
                        {getSectorIcon(sector.sector_ar)}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {getSectorText(sector, "sector")}
                        </h3>
                        <div className="flex items-center gap-2 text-white/90 text-sm">
                          <span className="bg-white/20 px-2 py-1 rounded-full backdrop-blur-sm">
                            {sector.companies?.length || 0}{" "}
                            {lng === "ar"
                              ? "شركة"
                              : lng === "fr"
                              ? "entreprises"
                              : "companies"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* قائمة الشركات */}
                  <div className="p-6">
                    <div className="space-y-3">
                      {companiesToShow?.map((company, companyIndex) => (
                        <motion.div
                          key={companyIndex}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 0.4,
                            delay: companyIndex * 0.05,
                          }}
                          viewport={{ once: true }}
                          onMouseEnter={() => setHoveredCompany(companyIndex)}
                          onMouseLeave={() => setHoveredCompany(null)}
                          onClick={() =>
                            goToProfile(company.id || companyIndex)
                          }
                          className="cursor-pointer group/company relative overflow-hidden rounded-xl bg-gray-50 hover:bg-white p-4 transition-all duration-300 border border-gray-100 hover:border-gray-200 hover:shadow-md"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              {company.logo && (
                                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm border border-gray-100">
                                  <img
                                    src={
                                      new URL(company.logo, import.meta.url)
                                        .href
                                    }
                                    alt={getCompanyName(company)}
                                    className="w-6 h-6 object-contain"
                                    onError={(e) =>
                                      (e.target.style.display = "none")
                                    }
                                  />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <span className="font-semibold text-gray-900 text-sm block truncate">
                                  {getCompanyName(company)}
                                </span>
                              </div>
                            </div>
                            <motion.div
                              animate={{
                                x:
                                  hoveredCompany === companyIndex
                                    ? isRTL
                                      ? -3
                                      : 3
                                    : 0,
                              }}
                              transition={{ duration: 0.2 }}
                            >
                              <ArrowRight
                                className={`w-4 h-4 ${sectorColor.bg} text-white rounded-full p-0.5`}
                              />
                            </motion.div>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* زر عرض المزيد */}
                    {sector.companies?.length > 4 && (
                      <div className="mt-6 pt-4 border-t  from-blue-600 to-cyan-500 text-white rounded-xl hover:from-blue-700 hover:to-cyan-600">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => toggleSectorExpansion(index)}
                          className={`w-full flex items-center justify-center gap-2 ${sectorColor.bg} hover:opacity-90 text-white px-4 py-2.5 rounded-lg font-medium transition-all duration-300 text-sm`}
                        >
                          {isExpanded ? (
                            <>
                              {lng === "ar"
                                ? "عرض أقل"
                                : lng === "fr"
                                ? "Voir moins"
                                : "Show Less"}
                              <ChevronUp className="w-3 h-3" />
                            </>
                          ) : (
                            <>
                              {lng === "ar"
                                ? "عرض الكل"
                                : lng === "fr"
                                ? "Voir tout"
                                : "View All"}
                              <ChevronDown className="w-3 h-3" />
                            </>
                          )}
                        </motion.button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {filteredSectors.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {lng === "ar"
                  ? "لا توجد نتائج"
                  : lng === "fr"
                  ? "Aucun résultat"
                  : "No results found"}
              </h3>
              <p className="text-gray-600">
                {lng === "ar"
                  ? "جرب البحث بكلمات أخرى أو اختر قطاع مختلف"
                  : lng === "fr"
                  ? "Essayez d'autres mots-clés ou choisissez un secteur différent"
                  : "Try different keywords or select another sector"}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      <style jsx>{`
        @keyframes pulse {
          0%,
          100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.6;
          }
        }
        .animate-pulse {
          animation: pulse 4s ease-in-out infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Sectors;
